import { z } from "zod";
import { normalizePatientName, patientNameAsTyped } from "./patients.js";
import { Centavos, checkDayRange, DayRangeFields, IsoDate, IsoDateTime, LongText, NonEmptyString, PageQuery, QueryBool, ShortText, SignedCentavos, Uuid } from "./common.js";

// Quotes, orders (solicitudes), discounts, payments, cash close, referring doctors.
// Balance due is derived, never stored. Money is centavos. Prices include IVA (the lab's own
// rule, meeting 2026-10-31): there is no tax line anywhere.

/**
 * A referring doctor's full name is stored like a patient's (the lab, 2026-11-03: doctors in capitals
 * "like patients"): the same rule, one function. Global, not a per-laboratory setting, exactly as
 * the patient rule is. Title prefixes survive ("Dra. Lucía" → "DRA. LUCIA").
 */
export const normalizeDoctorName = normalizePatientName;
/** What the doctor name field shows while typing; see `patientNameAsTyped`. */
export const doctorNameAsTyped = patientNameAsTyped;

const DOCTOR_TITLE = /^(DR|DRA|DRES|LIC|ING|QFB|Q\.?F\.?B|MC|M\.?C|PROF)\b\.?\s*/u;

/** Comparison key of a doctor name: normalised, title prefix and punctuation removed. "Dra. Lucía  González" → "LUCIA GONZALEZ". */
export function doctorNameKey(name: string): string {
  return normalizeDoctorName(name).replace(DOCTOR_TITLE, "").replace(/[.,]/gu, " ").replace(/\s+/gu, " ").trim();
}

/** True when `a` and `b` differ by exactly one inserted, deleted or substituted letter, or one adjacent swap. */
export function oneLetterApart(a: string, b: string): boolean {
  if (a === b) return false;
  const [s, l] = a.length <= b.length ? [a, b] : [b, a];
  if (l.length - s.length > 1) return false;
  let i = 0;
  while (i < s.length && s[i] === l[i]) i++;
  if (s.length === l.length) {
    if (s.slice(i + 1) === l.slice(i + 1)) return true;
    return s[i] === l[i + 1] && s[i + 1] === l[i] && s.slice(i + 2) === l.slice(i + 2);
  }
  return s.slice(i) === l.slice(i + 1);
}

/**
 * Whether a doctor being typed probably is an existing one (the "¿Es alguno de estos?" prompt of the
 * New doctor sheet): same name key; one letter apart on the whole name; or, with two or more typed
 * words, every typed word equals, starts, or is one letter from a word of the existing name.
 */
export function doctorNamesClose(typed: string, existing: string): boolean {
  const a = doctorNameKey(typed);
  const b = doctorNameKey(existing);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length >= 8 && b.length >= 8 && oneLetterApart(a, b)) return true;
  const typedWords = a.split(" ").filter((t) => t.length >= 3);
  if (typedWords.length < 2) return false;
  const words = b.split(" ");
  return typedWords.every((t) => words.some((u) => u === t || u.startsWith(t) || (t.length >= 5 && u.length >= 5 && oneLetterApart(t, u))));
}

export const Doctor = z.object({
  id: Uuid,
  full_name: NonEmptyString,
  cedula: ShortText.nullable(),
  specialty: ShortText.nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  is_active: z.boolean(),
});
export type Doctor = z.infer<typeof Doctor>;

export const DoctorUpsertPayload = Doctor.omit({ id: true }).partial({ is_active: true }).extend({
  doctor_id: Uuid.nullable().default(null),
});
export type DoctorUpsertPayload = z.infer<typeof DoctorUpsertPayload>;

export const DoctorListQuery = PageQuery.extend({
  q: z.string().trim().max(120).optional(),
  include_inactive: QueryBool.default(false),
});
export type DoctorListQuery = z.infer<typeof DoctorListQuery>;

/** Printed when no referring doctor is given. */
export const DOCTOR_NONE_TEXT = "A quien corresponda";

/** One line requested: a study or a package, at the price the device saw. */
export const OrderLineInput = z.object({
  kind: z.enum(["study", "package"]),
  id: Uuid,
  /** Price the client displayed; the server snapshots the current one and flags a mismatch. */
  expected_amount_cents: Centavos.nullable().default(null),
});
export type OrderLineInput = z.infer<typeof OrderLineInput>;

/**
 * A tube before it exists, by the key the server groups studies with: container and sample type
 * (null matches null). Used to mark a tube pending when the laboratory registers tubes as
 * collected at the counter (lab setting `samples_collected_at_registration`).
 */
export const TubeKey = z.object({ container_id: Uuid.nullable(), sample_type_id: Uuid.nullable() });
export type TubeKey = z.infer<typeof TubeKey>;
/**
 * Tubes the patient still owes. Only read when the laboratory has
 * `samples_collected_at_registration` on: every other new tube is then born collected. With the
 * setting off every tube is born pending, as always, and this list is ignored.
 */
const PendingTubes = z.array(TubeKey).max(50).default([]);

export const DiscountKind = z.enum(["fixed", "percent"]);
export type DiscountKind = z.infer<typeof DiscountKind>;
export const DiscountInput = z.object({
  kind: DiscountKind,
  /** Centavos for `fixed`; whole percent 1..100 for `percent`. The other figure is derived. */
  value: z.number().int().min(1),
  reason: ShortText.nullable().default(null),
});
export type DiscountInput = z.infer<typeof DiscountInput>;

/** How the discount was recorded, plus both derived figures for the screen and the ticket. */
export const DiscountApplied = z.object({
  kind: DiscountKind,
  value: z.number().int(),
  amount_cents: Centavos,
  /** Whole-number percent equivalent (rounded), for display. */
  percent: z.number().int(),
  reason: z.string().nullable(),
});
export type DiscountApplied = z.infer<typeof DiscountApplied>;

export const DeliveryChannel = z.enum(["whatsapp", "email", "print", "portal"]);
export type DeliveryChannel = z.infer<typeof DeliveryChannel>;

/** Lab identity block printed on tickets. Comes from the tenant row and its `settings`. */
export const LabIdentity = z.object({
  name: z.string(),
  rfc: z.string().nullable(),
  email: z.string().nullable(),
  results_url: z.string().nullable(),
  /** The lab's own logo as a `data:image/…` URI (tenant setting `logo_data_uri`), printed atop tickets. */
  logo_src: z.string().nullable(),
});
export type LabIdentity = z.infer<typeof LabIdentity>;

export const BranchIdentity = z.object({
  id: Uuid,
  name: z.string(),
  short_code: z.string(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
});
export type BranchIdentity = z.infer<typeof BranchIdentity>;

// ---- Quotes -----------------------------------------------------------------------------

export const QuoteCreatePayload = z.object({
  branch_id: Uuid,
  patient_id: Uuid.nullable().default(null),
  /** Free-text name when the person is not registered yet. */
  prospect_name: ShortText.nullable().default(null),
  price_list_id: Uuid.nullable().default(null),
  lines: z.array(OrderLineInput).min(1),
  discount: DiscountInput.nullable().default(null),
  /** Defaults to 15 days from the quote (the lab's printed rule). */
  valid_until: IsoDate.nullable().default(null),
  notes: LongText.nullable().default(null),
});
export type QuoteCreatePayload = z.infer<typeof QuoteCreatePayload>;

export const QuoteLine = z.object({
  kind: z.enum(["study", "package"]),
  id: Uuid,
  clave: z.string(),
  name: z.string(),
  amount_cents: Centavos,
  /** Preparation printed under the quote ("Ayuno de 8 horas"). */
  indications: z.string().nullable(),
  /** Studies inside a package line, for the ticket. */
  study_claves: z.array(z.string()),
});
export type QuoteLine = z.infer<typeof QuoteLine>;

export const QuoteState = z.enum(["open", "converted", "expired", "cancelled"]);

export const Quote = z.object({
  id: Uuid,
  folio: z.string(),
  branch_id: Uuid,
  patient_id: Uuid.nullable(),
  prospect_name: z.string().nullable(),
  price_list_id: Uuid.nullable(),
  lines: z.array(QuoteLine),
  subtotal_cents: Centavos,
  discount: DiscountApplied.nullable(),
  discount_cents: Centavos,
  total_cents: Centavos,
  valid_until: IsoDate.nullable(),
  notes: z.string().nullable(),
  state: QuoteState,
  converted_order_id: Uuid.nullable(),
  created_at: IsoDateTime,
  created_by: Uuid,
});
export type Quote = z.infer<typeof Quote>;

export const QuoteListQuery = PageQuery.extend({
  branch_id: Uuid.optional(),
  patient_id: Uuid.optional(),
  state: QuoteState.optional(),
  q: z.string().trim().max(120).optional(),
});
export type QuoteListQuery = z.infer<typeof QuoteListQuery>;

export const QuoteConvertPayload = z.object({
  quote_id: Uuid,
  /** Required when the quote had no registered patient. */
  patient_id: Uuid.nullable().default(null),
  doctor_id: Uuid.nullable().default(null),
  doctor_text: ShortText.nullable().default(null),
  delivery_channels: z.array(DeliveryChannel).min(1).default(["print"]),
  clinical_notes: LongText.nullable().default(null),
  fasting_hours: z.number().int().min(0).max(48).nullable().default(null),
  condition: ShortText.nullable().default(null),
  pending_tubes: PendingTubes,
});
export type QuoteConvertPayload = z.infer<typeof QuoteConvertPayload>;

/** Everything the quote ticket prints (thermal 58/80 mm). */
export const QuoteTicket = z.object({
  lab: LabIdentity,
  branch: BranchIdentity,
  quote: Quote,
  customer_name: z.string(),
  /** "Cotización válida por 15 días, precios sujetos a cambio sin previo aviso." */
  validity_text: z.string(),
  indications: z.array(z.string()),
  issued_by: z.string(),
});
export type QuoteTicket = z.infer<typeof QuoteTicket>;

// ---- Orders -----------------------------------------------------------------------------

export const OrderState = z.enum([
  "registered",
  "collected",
  "in_process",
  "reported_partial",
  "reported_final",
  "closed",
  "cancelled",
]);
export type OrderState = z.infer<typeof OrderState>;

export const OrderItemState = z.enum(["pending", "captured", "validated", "issued", "amended", "cancelled"]);
export type OrderItemState = z.infer<typeof OrderItemState>;

export const OrderCreatePayload = z.object({
  branch_id: Uuid,
  patient_id: Uuid,
  doctor_id: Uuid.nullable().default(null),
  /** Free-text doctor; null and no doctor_id prints "A quien corresponda". */
  doctor_text: ShortText.nullable().default(null),
  company_id: Uuid.nullable().default(null),
  price_list_id: Uuid.nullable().default(null),
  lines: z.array(OrderLineInput).min(1),
  discount: DiscountInput.nullable().default(null),
  delivery_channels: z.array(DeliveryChannel).min(1).default(["print"]),
  /** Clinical support notes: reason, medication, LMP, pregnancy weeks. Staff-only. */
  clinical_notes: LongText.nullable().default(null),
  /** Hours fasting as declared at the counter; null = not asked / not required. */
  fasting_hours: z.number().int().min(0).max(48).nullable().default(null),
  /** Special condition that selects reference intervals: "embarazo", … Null = none. */
  condition: ShortText.nullable().default(null),
  /** Catalogue versions the client had cached, for the snapshot rule (§4.1 item 2). */
  catalogue_seen_at: IsoDateTime.nullable().default(null),
  pending_tubes: PendingTubes,
});
export type OrderCreatePayload = z.infer<typeof OrderCreatePayload>;

export const OrderAddItemsPayload = z.object({
  order_id: Uuid,
  lines: z.array(OrderLineInput).min(1),
  /** Only new tubes are affected: a study that fits a tube already on the order keeps that tube's state. */
  pending_tubes: PendingTubes,
});
export type OrderAddItemsPayload = z.infer<typeof OrderAddItemsPayload>;

/**
 * Cancel an order. With `void_payments` the order's active payments are voided in the same
 * transaction with the same reason (the web dialog lists them first); without it an order with
 * money received is refused. A payment already in a cash close refuses the whole cancellation.
 */
export const OrderCancelPayload = z.object({ order_id: Uuid, reason: NonEmptyString, void_payments: z.boolean().default(false) });

/**
 * Remove one study registered by mistake. The handler refuses it once the study has any captured
 * value, and refuses the order's last live study (that is `order.cancel`).
 */
export const OrderItemCancelPayload = z.object({ order_item_id: Uuid, reason: NonEmptyString });

/**
 * Correct the order's header after registration. Every field is optional: absent means
 * unchanged. `doctor_id` and `doctor_text` travel as a pair (either present replaces both; the
 * missing one becomes null). `condition` re-selects the reference intervals, so the handler
 * refuses to change it once any study of the order has a captured value.
 */
export const OrderUpdateHeaderPayload = z.object({
  order_id: Uuid,
  doctor_id: Uuid.nullable().optional(),
  doctor_text: ShortText.nullable().optional(),
  delivery_channels: z.array(DeliveryChannel).min(1).optional(),
  clinical_notes: LongText.nullable().optional(),
  fasting_hours: z.number().int().min(0).max(48).nullable().optional(),
  condition: ShortText.nullable().optional(),
});
export type OrderUpdateHeaderPayload = z.infer<typeof OrderUpdateHeaderPayload>;

export const OrderItem = z.object({
  id: Uuid,
  order_id: Uuid,
  study_id: Uuid,
  package_id: Uuid.nullable(),
  study_clave: z.string(),
  study_name: z.string(),
  area_name: z.string().nullable(),
  /** The frozen study, analytes, units and the interval rows applied for this patient. */
  study_snapshot: z.record(z.string(), z.unknown()),
  price_cents: Centavos,
  state: OrderItemState,
  price_mismatch: z.boolean(),
  cancelled_reason: z.string().nullable(),
  /** Label code of the tube this study is drawn into, when it needs a sample. */
  sample_label_code: z.string().nullable(),
  /** Any value captured for this study, even a partial capture still `pending`. */
  has_results: z.boolean(),
  /** Promised delivery day (lab-local) from the snapshot's delivery rule and the sample's receipt (ADR 0027). */
  due_date: IsoDate.nullable().optional(),
});
export type OrderItem = z.infer<typeof OrderItem>;

/** Tube summary inside an order (the full `Sample` lives in work.ts). */
export const OrderSample = z.object({
  id: Uuid,
  seq: z.number().int(),
  label_code: z.string(),
  container_name: z.string().nullable(),
  container_colour_hex: z.string().nullable(),
  container_colour_name: z.string().nullable(),
  sample_type_name: z.string().nullable(),
  study_claves: z.array(z.string()),
  state: z.enum(["pending", "collected", "received", "processed", "rejected"]),
});
export type OrderSample = z.infer<typeof OrderSample>;

export const Order = z.object({
  id: Uuid,
  folio: z.string(),
  branch_id: Uuid,
  patient_id: Uuid,
  doctor_id: Uuid.nullable(),
  doctor_text: z.string().nullable(),
  /** Resolved for display: the doctor's name, the free text, or "A quien corresponda". */
  doctor_display: z.string(),
  company_id: Uuid.nullable(),
  price_list_id: Uuid.nullable(),
  quote_id: Uuid.nullable(),
  state: OrderState,
  subtotal_cents: Centavos,
  discount: DiscountApplied.nullable(),
  discount_cents: Centavos,
  total_cents: Centavos,
  /** Real money received (voided and patient-credit entries excluded). */
  paid_cents: Centavos,
  /** Derived: total − paid. */
  balance_cents: Centavos,
  /** Any tube still `pending`: the red flag on the worklist. */
  has_pending_samples: z.boolean(),
  /** Tubes of the order, whatever their state: Solicitudes' «Imprimir etiquetas» needs at least one. */
  samples_count: z.number().int().min(0),
  /** Promised delivery: the latest due day of its live studies (ADR 0027); null when none promises one. */
  due_date: IsoDate.nullable().optional(),
  delivery_channels: z.array(DeliveryChannel),
  clinical_notes: z.string().nullable(),
  fasting_hours: z.number().int().nullable(),
  condition: z.string().nullable(),
  cancelled_reason: z.string().nullable(),
  /** Null only on history migrated from the previous system (migration 0008). */
  registered_by: Uuid.nullable(),
  /** 'app' for orders registered here; 'migrated' for the previous system's history. */
  origin: z.enum(["app", "migrated"]),
  /** The previous system's folio, on migrated orders. */
  external_folio: z.string().nullable(),
  occurred_at: IsoDateTime,
  created_at: IsoDateTime,
});
export type Order = z.infer<typeof Order>;

export const OrderPatient = z.object({
  id: Uuid,
  first_name: z.string(),
  paternal_surname: z.string(),
  maternal_surname: z.string().nullable(),
  birth_date: IsoDate,
  sex: z.enum(["M", "F"]),
  phone: z.string().nullable(),
  /** "27 años", "8 meses", "12 días" at the order's date. */
  age_text: z.string(),
});

export const OrderDetail = Order.extend({
  items: z.array(OrderItem),
  samples: z.array(OrderSample),
  patient: OrderPatient,
});
export type OrderDetail = z.infer<typeof OrderDetail>;

export const OrderListQuery = PageQuery.extend({
  branch_id: Uuid.optional(),
  patient_id: Uuid.optional(),
  state: OrderState.optional(),
  /** Registration day or range in America/Mexico_City; default = every day. */
  ...DayRangeFields,
  pending_samples: QueryBool.optional(),
  with_balance: QueryBool.optional(),
  q: z.string().trim().max(120).optional(),
}).superRefine(checkDayRange);
export type OrderListQuery = z.infer<typeof OrderListQuery>;

// ---- Payments ---------------------------------------------------------------------------

export const PaymentMethod = z.enum([
  "efectivo",
  "tarjeta_debito",
  "tarjeta_credito",
  "transferencia",
  /** Patient credit: recorded, but the balance stays due and results are held until settled. */
  "credito_paciente",
]);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const PaymentMethodLabel: Record<PaymentMethod, string> = {
  efectivo: "Efectivo",
  tarjeta_debito: "Tarjeta de débito",
  tarjeta_credito: "Tarjeta de crédito",
  transferencia: "Transferencia",
  credito_paciente: "Crédito al paciente",
};

/** Methods that are money in the drawer or the bank; credit is a promise, not a payment. */
export const CASH_LIKE_METHODS: readonly PaymentMethod[] = ["efectivo", "tarjeta_debito", "tarjeta_credito", "transferencia"];

export const PaymentRecordPayload = z.object({
  order_id: Uuid,
  branch_id: Uuid.nullable().default(null),
  method: PaymentMethod,
  amount_cents: Centavos.min(1),
  /** Card voucher, transfer reference. */
  reference: ShortText.nullable().default(null),
  /** Cash tendered, to compute and print the change. */
  tendered_cents: Centavos.nullable().default(null),
});
export type PaymentRecordPayload = z.infer<typeof PaymentRecordPayload>;

export const PaymentVoidPayload = z.object({ payment_id: Uuid, reason: NonEmptyString });

/**
 * Correct how a payment was paid (Fernanda, 2026-11-02: recorded as cash, paid with a debit card).
 * Amount, date, receiver and order balance stay as they are; only method, reference and, for cash,
 * what the patient handed over change. Never between money and `credito_paciente`.
 */
export const PaymentCorrectMethodPayload = z.object({
  payment_id: Uuid,
  method: PaymentMethod,
  reference: ShortText.nullable().default(null),
  /** Only for cash; ignored otherwise. */
  tendered_cents: Centavos.nullable().default(null),
});
export type PaymentCorrectMethodPayload = z.infer<typeof PaymentCorrectMethodPayload>;

export const Payment = z.object({
  id: Uuid,
  order_id: Uuid,
  branch_id: Uuid,
  method: PaymentMethod,
  amount_cents: Centavos,
  reference: z.string().nullable(),
  tendered_cents: Centavos.nullable(),
  /** tendered − amount, for cash. */
  change_cents: Centavos.nullable(),
  /** Null only on payments migrated from the previous system (migration 0008). */
  received_by: Uuid.nullable(),
  received_by_name: z.string(),
  occurred_at: IsoDateTime,
  voided_at: IsoDateTime.nullable(),
  void_reason: z.string().nullable(),
  cash_close_id: Uuid.nullable(),
});
export type Payment = z.infer<typeof Payment>;

/** What prints on the sale ticket, modelled on the lab's current ticket. */
export const SaleTicket = z.object({
  lab: LabIdentity,
  branch: BranchIdentity,
  order: Order,
  patient_name: z.string(),
  patient_age_text: z.string(),
  patient_phone: z.string().nullable(),
  items: z.array(
    z.object({
      study_clave: z.string(),
      study_name: z.string(),
      price_cents: Centavos,
      /** Promised delivery date, YYYY-MM-DD, from the study's delivery_days. */
      delivery_date: IsoDate.nullable(),
      package_name: z.string().nullable(),
    }),
  ),
  /** Non-voided payments, oldest first ("Abono 1", "Abono 2"…). */
  payments: z.array(Payment),
  /** Sum of tendered cash minus the cash amounts, for the "Paga con / Cambio" lines. */
  tendered_cents: Centavos.nullable(),
  change_cents: Centavos.nullable(),
  payment_methods_text: z.string().nullable(),
  seller_name: z.string(),
  indications: z.array(z.string()),
  /** Opaque token for the portal QR; null until the portal exists. */
  portal_token: z.string().nullable(),
  /** Text encoded in the ticket QR (the results URL + folio for now). */
  qr_text: z.string().nullable(),
});
export type SaleTicket = z.infer<typeof SaleTicket>;

/** Thermal paper width for the HTML renderings. */
export const TicketQuery = z.object({ width: z.enum(["58", "80"]).default("80") });
export type TicketQuery = z.infer<typeof TicketQuery>;

// ---- Cash movements (extra income / expense in the drawer) ------------------------------

export const CashMovementKind = z.enum(["ingreso", "egreso"]);
export type CashMovementKind = z.infer<typeof CashMovementKind>;
export const CashMovementKindLabel: Record<CashMovementKind, string> = { ingreso: "Ingreso", egreso: "Egreso" };

/** The money methods of a payment: patient credit is a promise, not money, so it is not a movement. */
export const CashMovementMethod = PaymentMethod.exclude(["credito_paciente"]);
export type CashMovementMethod = z.infer<typeof CashMovementMethod>;

/**
 * Pilot request 2026-11-03 (Laboratorio Sonder, Fer 09:23): extra income or expense in the
 * corte de caja. It belongs to the drawer of the acting user at the branch (like a payment) and
 * to the close that includes it; the concept is typed (suggestions come from earlier ones).
 */
export const CashMovementRecordPayload = z.object({
  branch_id: Uuid,
  kind: CashMovementKind,
  concept: z.string().trim().min(1).max(120),
  amount_cents: Centavos.min(1),
  method: CashMovementMethod,
  note: ShortText.nullable().default(null),
});
export type CashMovementRecordPayload = z.infer<typeof CashMovementRecordPayload>;

/** A movement is never deleted: it is voided with a reason, before the close that would include it. */
export const CashMovementVoidPayload = z.object({ movement_id: Uuid, reason: NonEmptyString });
export type CashMovementVoidPayload = z.infer<typeof CashMovementVoidPayload>;

export const CashMovement = z.object({
  id: Uuid,
  branch_id: Uuid,
  kind: CashMovementKind,
  concept: z.string(),
  amount_cents: Centavos,
  method: CashMovementMethod,
  note: z.string().nullable(),
  registered_by: Uuid,
  registered_by_name: z.string(),
  occurred_at: IsoDateTime,
  voided_at: IsoDateTime.nullable(),
  void_reason: z.string().nullable(),
  cash_close_id: Uuid.nullable(),
});
export type CashMovement = z.infer<typeof CashMovement>;

/** The movements of one drawer in a period, voided ones included (flagged), oldest first. */
export const CashMovementListQuery = z.object({
  branch_id: Uuid,
  user_id: Uuid.optional(),
  period_start: IsoDateTime,
  period_end: IsoDateTime,
});
export type CashMovementListQuery = z.infer<typeof CashMovementListQuery>;

/** Earlier concepts, most used first, to suggest while typing. */
export const CashMovementConceptsQuery = z.object({ kind: CashMovementKind.optional() });
export type CashMovementConceptsQuery = z.infer<typeof CashMovementConceptsQuery>;
export const CashMovementConcepts = z.object({ items: z.array(z.string()) });
export type CashMovementConcepts = z.infer<typeof CashMovementConcepts>;

// ---- Cash close -------------------------------------------------------------------------

export const CashCloseCreatePayload = z.object({
  branch_id: Uuid,
  /** Whose drawer is being closed; null = the acting user. Closing another user's needs Administrador. */
  user_id: Uuid.nullable().default(null),
  period_start: IsoDateTime,
  period_end: IsoDateTime,
  /** Cash physically counted. */
  counted_cash_cents: Centavos,
  notes: LongText.nullable().default(null),
});
export type CashCloseCreatePayload = z.infer<typeof CashCloseCreatePayload>;

export const CashCloseSummary = z.object({
  branch_id: Uuid,
  user_id: Uuid,
  user_name: z.string(),
  period_start: IsoDateTime,
  period_end: IsoDateTime,
  by_method: z.array(z.object({ method: PaymentMethod, count: z.number().int(), amount_cents: Centavos })),
  /** Payments across all money methods (credit excluded); movements are apart, below. */
  total_cents: Centavos,
  /**
   * Cash only: what should be in the drawer = cash payments + cash ingresos - cash egresos.
   * Signed: a drawer whose only activity is an expense is below zero.
   */
  expected_cash_cents: SignedCentavos,
  payments_count: z.number().int(),
  orders_count: z.number().int(),
  /** Credit granted in the period: informational, never money. */
  credit_granted_cents: Centavos,
  /** Payments not yet included in any close. */
  unclosed_payment_ids: z.array(Uuid),
  // Cash movements (migration 0030). Defaults keep the summaries stored in closes made before it valid.
  /** Non-voided movements by money method, only methods with activity. */
  movements_by_method: z.array(z.object({ method: CashMovementMethod, count: z.number().int(), income_cents: Centavos, expense_cents: Centavos })).default([]),
  movements_count: z.number().int().default(0),
  movements_income_cents: Centavos.default(0),
  movements_expense_cents: Centavos.default(0),
  /** Payments + ingresos - egresos per money method (only methods with activity), and their sum. */
  net_by_method: z.array(z.object({ method: CashMovementMethod, amount_cents: SignedCentavos })).default([]),
  net_total_cents: SignedCentavos.default(0),
  /** Movements not yet included in any close. */
  unclosed_movement_ids: z.array(Uuid).default([]),
});
export type CashCloseSummary = z.infer<typeof CashCloseSummary>;

export const CashClose = z.object({
  id: Uuid,
  branch_id: Uuid,
  user_id: Uuid,
  closed_by: Uuid,
  period_start: IsoDateTime,
  period_end: IsoDateTime,
  summary: CashCloseSummary,
  counted_cash_cents: Centavos,
  expected_cash_cents: SignedCentavos,
  variance_cents: SignedCentavos,
  notes: z.string().nullable(),
  created_at: IsoDateTime,
});
export type CashClose = z.infer<typeof CashClose>;

export const CashCloseQuery = z.object({
  branch_id: Uuid,
  user_id: Uuid.optional(),
  period_start: IsoDateTime,
  period_end: IsoDateTime,
});
export type CashCloseQuery = z.infer<typeof CashCloseQuery>;

export const CashCloseListQuery = PageQuery.extend({ branch_id: Uuid.optional(), user_id: Uuid.optional() });

/** How a cash close prints: on the ticket roll (80/58 mm) or on a letter sheet from a desktop PC. */
export const CashClosePrintFormat = z.enum(["80", "58", "letter"]);
export type CashClosePrintFormat = z.infer<typeof CashClosePrintFormat>;
export const CashClosePrintQuery = z.object({ format: CashClosePrintFormat.default("80") });
export type CashClosePrintQuery = z.infer<typeof CashClosePrintQuery>;
/** The live drawer (before closing) printed with the same filters as `cashCloseSummary`. */
export const CashCloseSummaryPrintQuery = CashCloseQuery.extend({ format: CashClosePrintFormat.default("80") });
export type CashCloseSummaryPrintQuery = z.infer<typeof CashCloseSummaryPrintQuery>;

/** Everything a printed cash close shows; `close` is null for the live summary (nothing counted yet). */
export const CashClosePrint = z.object({
  lab: LabIdentity,
  branch: BranchIdentity,
  summary: CashCloseSummary,
  close: z
    .object({
      id: Uuid,
      closed_by_name: z.string(),
      created_at: IsoDateTime,
      counted_cash_cents: Centavos,
      expected_cash_cents: SignedCentavos,
      variance_cents: SignedCentavos,
      notes: z.string().nullable(),
    })
    .nullable(),
  /** Payments of the drawer in the period that were voided (never in the totals). */
  voided: z.object({ count: z.number().int(), amount_cents: Centavos }),
  /** The drawer's non-voided movements in the close (or, live, in the period), oldest first. */
  movements: z.array(z.object({ kind: CashMovementKind, concept: z.string(), method: CashMovementMethod, amount_cents: Centavos, occurred_at: IsoDateTime })).default([]),
  /** Movements of the drawer in the period that were voided (never in the totals). */
  voided_movements: z.object({ count: z.number().int(), amount_cents: Centavos }).default({ count: 0, amount_cents: 0 }),
  printed_at: IsoDateTime,
});
export type CashClosePrint = z.infer<typeof CashClosePrint>;

// ---- Doctor report (referrals per doctor, a period; no commissions) ---------------------

export const DoctorReportQuery = z.object({
  branch_id: Uuid.optional(),
  from: IsoDate,
  to: IsoDate,
  doctor_id: Uuid.optional(),
});
export type DoctorReportQuery = z.infer<typeof DoctorReportQuery>;

export const DoctorReportRow = z.object({
  doctor_id: Uuid.nullable(),
  doctor_name: z.string(),
  orders_count: z.number().int(),
  studies_count: z.number().int(),
  total_cents: Centavos,
  paid_cents: Centavos,
});
export const DoctorReport = z.object({ rows: z.array(DoctorReportRow), from: IsoDate, to: IsoDate });
export type DoctorReport = z.infer<typeof DoctorReport>;
